import { pantryColors } from '@pantry/ui'
import React, { useMemo, useState } from 'react'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import type { ProjectionConfig } from 'react-simple-maps'
import type {
  LoyaltyGeoCountryRow,
  LoyaltyGeoRegionRow,
  LoyaltyMetricsRange,
} from '../../../../../types/loyaltyTracking'
import { useLoyaltyGeoQuery } from '../../../hooks/useLoyaltyTracking'
import {
  PanelEmpty,
  PanelError,
  PanelLoading,
  formatCount,
  panelStyle,
  panelSubtitleStyle,
  panelTitleStyle,
} from './analyticsCardShared'
// Vendored topojson (no runtime CDN fetches): world countries from
// world-atlas@2 (Natural Earth 110m), US states from us-atlas@3 (10m), Canada
// provinces built from Natural Earth 50m admin-1 boundaries. Every geometry
// carries `properties.{name, code}` — alpha-2 for countries, postal/ISO
// region code for states and provinces.
import canadaProvinces from './geo/canada-provinces-50m.json'
import usStates from './geo/us-states-10m.json'
import worldCountries from './geo/world-countries-110m.json'

const p = pantryColors

// Render height of the choropleth, in px. Caps how tall the Visitor
// locations card can get.
const MAP_HEIGHT = 500

// Countries with a bundled sub-national topojson → drilling in shows a
// state/province choropleth. Any other country shows a region table instead.
const MAP_COUNTRIES: Record<
  string,
  { label: string; geography: Record<string, unknown> }
> = {
  US: { label: 'United States', geography: usStates },
  CA: { label: 'Canada', geography: canadaProvinces },
}

// ── Light→accent-blue color scale (pantry brand hues) ────────────────────────

const hexToRgb = (hex: string): [number, number, number] => [
  Number.parseInt(hex.slice(1, 3), 16),
  Number.parseInt(hex.slice(3, 5), 16),
  Number.parseInt(hex.slice(5, 7), 16),
]

const SCALE_FROM = hexToRgb(p.brand.light) // #EFF6FF
const SCALE_TO = hexToRgb(p.brand.primary) // #2563EB

// t ∈ [0,1] → hex between the light and accent brand blues. A sqrt ease keeps
// low-volume regions visible when one region dominates the counts.
const scaleColor = (count: number, max: number): string => {
  if (count <= 0 || max <= 0) return p.gray[100]
  const t = Math.sqrt(Math.min(1, count / max))
  const channel = (i: number) =>
    Math.round(SCALE_FROM[i] + (SCALE_TO[i] - SCALE_FROM[i]) * t)
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`
}

// ── Row-shape helpers (country rows vs region rows) ──────────────────────────

const isCountryRow = (row: unknown): row is LoyaltyGeoCountryRow =>
  Boolean(row) && typeof (row as LoyaltyGeoCountryRow).country_code === 'string'

const isRegionRow = (row: unknown): row is LoyaltyGeoRegionRow =>
  Boolean(row) && typeof (row as LoyaltyGeoRegionRow).region_code === 'string'

interface TooltipState {
  name: string
  count: number
  x: number
  y: number
}

interface ChoroplethProps {
  geography: Record<string, unknown>
  counts: Record<string, number>
  maxCount: number
  projection: string
  projectionConfig?: ProjectionConfig
  onHover: (tooltip: TooltipState | null) => void
}

// One choropleth surface: world / US / Canada all render through this.
const Choropleth: React.FC<ChoroplethProps> = ({
  geography,
  counts,
  maxCount,
  projection,
  projectionConfig,
  onHover,
}) => (
  <ComposableMap
    projection={projection}
    projectionConfig={projectionConfig}
    // Fixed render height instead of `height: 'auto'`: the SVG's intrinsic
    // aspect ratio made the card grow very tall on wide viewports. The default
    // preserveAspectRatio ("xMidYMid meet") letterboxes the projection inside
    // this box, so the map stays centred and undistorted at any width.
    style={{ width: '100%', height: MAP_HEIGHT }}
  >
    <Geographies geography={geography}>
      {({ geographies }) =>
        geographies.map((geo) => {
          const code: string = geo.properties?.code ?? ''
          const name: string = geo.properties?.name ?? ''
          const count = counts[code] ?? 0
          return (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              fill={scaleColor(count, maxCount)}
              stroke={p.white}
              strokeWidth={0.5}
              style={{
                default: { outline: 'none' },
                hover: { outline: 'none', fill: p.brand.mid },
                pressed: { outline: 'none' },
              }}
              onMouseMove={(event: React.MouseEvent) =>
                onHover({ name, count, x: event.clientX, y: event.clientY })
              }
              onMouseLeave={() => onHover(null)}
            />
          )
        })
      }
    </Geographies>
  </ComposableMap>
)

// Region rows table for countries without a bundled sub-national map (and the
// side list next to the world map).
const RegionTable: React.FC<{ rows: LoyaltyGeoRegionRow[] }> = ({ rows }) => (
  <div
    style={{
      border: `1px solid ${p.gray[100]}`,
      borderRadius: 8,
      overflow: 'auto',
      maxHeight: 320,
    }}
  >
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th
            style={{
              textAlign: 'left',
              padding: '8px 12px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: p.gray[400],
              borderBottom: `1px solid ${p.gray[200]}`,
              background: p.gray[50],
            }}
          >
            Region
          </th>
          <th
            style={{
              textAlign: 'right',
              padding: '8px 12px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: p.gray[400],
              borderBottom: `1px solid ${p.gray[200]}`,
              background: p.gray[50],
              whiteSpace: 'nowrap',
            }}
          >
            Events
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.region_code || row.region}>
            <td
              style={{
                padding: '8px 12px',
                fontSize: 13,
                fontWeight: 600,
                color: p.gray[700],
                borderBottom: `1px solid ${p.gray[100]}`,
              }}
            >
              {row.region || row.region_code || '—'}
            </td>
            <td
              style={{
                padding: '8px 12px',
                fontSize: 13,
                color: p.gray[700],
                textAlign: 'right',
                borderBottom: `1px solid ${p.gray[100]}`,
                whiteSpace: 'nowrap',
              }}
            >
              {formatCount(row.count)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

interface GeoMapCardProps {
  storeId: string
  range: LoyaltyMetricsRange
}

// Geo distribution card: global country choropleth by default; selecting the
// US or Canada swaps in a bundled state/province choropleth, any other country
// shows its region table beside the world map.
export const GeoMapCard: React.FC<GeoMapCardProps> = ({ storeId, range }) => {
  // '' = All countries (world view).
  const [selectedCountry, setSelectedCountry] = useState('')
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  // World counts always load: they paint the default view and populate the
  // country selector. The drill-in query only fires once a country is picked
  // (it shares the world cache entry when none is).
  const worldQuery = useLoyaltyGeoQuery(storeId, range)
  const regionQuery = useLoyaltyGeoQuery(
    storeId,
    range,
    selectedCountry || undefined,
  )

  const countryRows = useMemo(
    () => (worldQuery.data ?? []).filter(isCountryRow),
    [worldQuery.data],
  )
  const regionRows = useMemo(
    () => (selectedCountry ? (regionQuery.data ?? []) : []).filter(isRegionRow),
    [selectedCountry, regionQuery.data],
  )

  // Selector options: every country present in the data, plus US/CA always
  // (they have bundled maps), sorted by event volume.
  const countryOptions = useMemo(() => {
    const byCode = new Map<string, { label: string; count: number }>()
    for (const row of countryRows) {
      if (!row.country_code) continue
      byCode.set(row.country_code, {
        label: row.country || row.country_code,
        count: row.count,
      })
    }
    for (const [code, entry] of Object.entries(MAP_COUNTRIES)) {
      if (!byCode.has(code)) byCode.set(code, { label: entry.label, count: 0 })
    }
    return Array.from(byCode.entries())
      .map(([code, entry]) => ({ code, ...entry }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
  }, [countryRows])

  const countryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const row of countryRows) counts[row.country_code] = row.count
    return counts
  }, [countryRows])

  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const row of regionRows) counts[row.region_code] = row.count
    return counts
  }, [regionRows])

  const maxCountryCount = countryRows.reduce(
    (max, row) => Math.max(max, row.count),
    0,
  )
  const maxRegionCount = regionRows.reduce(
    (max, row) => Math.max(max, row.count),
    0,
  )

  const mapCountry = selectedCountry
    ? MAP_COUNTRIES[selectedCountry]
    : undefined
  const isLoading =
    worldQuery.isLoading || (Boolean(selectedCountry) && regionQuery.isLoading)
  const isError =
    worldQuery.isError || (Boolean(selectedCountry) && regionQuery.isError)

  return (
    <div style={{ ...panelStyle, position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={panelTitleStyle}>Visitor locations</div>
          <div style={panelSubtitleStyle}>
            Where tracked events happen, by country and region.
          </div>
        </div>
        <select
          value={selectedCountry}
          onChange={(event) => {
            setSelectedCountry(event.target.value)
            setTooltip(null)
          }}
          style={{
            padding: '6px 10px',
            border: `1px solid ${p.gray[200]}`,
            borderRadius: 8,
            fontSize: 12.5,
            color: p.gray[700],
            background: p.white,
            outline: 'none',
            maxWidth: 200,
          }}
        >
          <option value="">All countries</option>
          {countryOptions.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <PanelLoading />
      ) : isError ? (
        <PanelError />
      ) : !selectedCountry && countryRows.length === 0 ? (
        <div style={{ marginTop: 12 }}>
          <PanelEmpty text="No location data in this range" />
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {mapCountry ? (
            // US / Canada: bundled state-or-province choropleth.
            regionRows.length === 0 ? (
              <PanelEmpty text="No regional data for this country yet" />
            ) : (
              <Choropleth
                geography={mapCountry.geography}
                counts={regionCounts}
                maxCount={maxRegionCount}
                projection={
                  selectedCountry === 'US'
                    ? 'geoAlbersUsa'
                    : 'geoConicConformal'
                }
                projectionConfig={
                  selectedCountry === 'US'
                    ? { scale: 900 }
                    : {
                        rotate: [96, 0, 0],
                        center: [0, 63],
                        parallels: [49, 77],
                        scale: 480,
                      }
                }
                onHover={setTooltip}
              />
            )
          ) : (
            // World view — with a region table beside it when a country
            // without a bundled map is selected.
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '2 1 360px', minWidth: 280 }}>
                <Choropleth
                  geography={worldCountries}
                  counts={countryCounts}
                  maxCount={maxCountryCount}
                  projection="geoEqualEarth"
                  onHover={setTooltip}
                />
              </div>
              {selectedCountry ? (
                <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: p.gray[700],
                      marginBottom: 8,
                    }}
                  >
                    {countryOptions.find((o) => o.code === selectedCountry)
                      ?.label ?? selectedCountry}{' '}
                    regions
                  </div>
                  {regionRows.length === 0 ? (
                    <PanelEmpty text="No regional data for this country yet" />
                  ) : (
                    <RegionTable rows={regionRows} />
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {tooltip ? (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x + 12,
            top: tooltip.y + 12,
            zIndex: 1500,
            pointerEvents: 'none',
            background: p.gray[900],
            color: p.white,
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 12,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
          }}
        >
          <span style={{ fontWeight: 600 }}>{tooltip.name || '—'}</span>
          {' · '}
          {formatCount(tooltip.count)} events
        </div>
      ) : null}
    </div>
  )
}
